import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { adminService, auditService, apiKeysService, organizationService, accessRequestService, demoAdminService } from '../services/api';
import { formatDate } from '../services/dateUtils';
import '../styles/AdminUsers.css';

export default function AdminUsers() {
  const navigate = useNavigate();
  const { userId, username: myUsername, role: myRole } = useAuthStore();
  const isSuperuser = myRole === 'superuser';

  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser]   = useState({ username: '', email: '', password: '', role: 'investigator', organization_id: '' });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [activeTab, setActiveTab]   = useState('users'); // 'users' | 'organizations' | 'requests' | 'demo' | 'audit' | 'apikeys'
  const [auditLogs, setAuditLogs]   = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter]   = useState({ action: '', username: '' });
  const [auditTotal, setAuditTotal]     = useState(0);
  const [purgeDays, setPurgeDays]       = useState(90);
  const [purging, setPurging]           = useState(false);
  const [purgeMessage, setPurgeMessage] = useState(null);

  // Organizations tab state (Superuser only — the tab itself is hidden for
  // anyone else, but state lives here regardless for simplicity)
  const [orgs, setOrgs]                 = useState([]);
  const [orgsLoading, setOrgsLoading]   = useState(false);
  const [newOrg, setNewOrg]             = useState({ name: '', website: '', contact_email: '' });
  const [creatingOrg, setCreatingOrg]   = useState(false);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editValues, setEditValues]     = useState({ name: '', website: '', contact_email: '' });

  // API Keys tab state
  const [apiKeys, setApiKeys]           = useState({});       // current masked status from server
  const [apiKeyInputs, setApiKeyInputs] = useState({});       // in-progress edits, keyed by env var name
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysSaving, setApiKeysSaving]   = useState(false);
  const [apiKeysMessage, setApiKeysMessage] = useState(null);

  // Access Requests tab state (Superuser only) — public "request access"
  // submissions from the marketing site, reviewed here before an Organization
  // is ever created for the requester.
  const [requests, setRequests]             = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsFilter, setRequestsFilter] = useState('pending'); // pending | approved | rejected | '' (all)
  const [requestActionId, setRequestActionId] = useState(null);    // id currently mid-approve/reject
  const [approvingId, setApprovingId]       = useState(null);      // id whose approve-form is expanded
  const [approveOrgName, setApproveOrgName] = useState('');
  const [rejectingId, setRejectingId]       = useState(null);      // id whose reject-form is expanded
  const [rejectNote, setRejectNote]         = useState('');
  const [requestsMessage, setRequestsMessage] = useState(null);

  // Demo Accounts tab state (Superuser only) — free 5-day trial accounts, see
  // App/api/routers/demo.py (public signup) and App/api/routers/admin.py
  // (open/close, feedback).
  const [demoAccounts, setDemoAccounts]     = useState([]);
  const [demoLoading, setDemoLoading]       = useState(false);
  const [demoActionId, setDemoActionId]     = useState(null);  // id currently mid-open/close
  const [demoMessage, setDemoMessage]       = useState(null);
  const [demoFeedback, setDemoFeedback]         = useState([]);
  const [demoFeedbackLoading, setDemoFeedbackLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listUsers();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const loadRequests = useCallback(async (status = requestsFilter) => {
    if (!isSuperuser) return;
    try {
      setRequestsLoading(true);
      setError(null);
      const res = await accessRequestService.list(status || null);
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load access requests');
    } finally {
      setRequestsLoading(false);
    }
  }, [isSuperuser, requestsFilter]);

  useEffect(() => {
    if (activeTab === 'requests') loadRequests();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pending requests once up front too, so the tab badge shows a count
  // even before the Superuser visits the tab — same pattern as orgs preload above.
  useEffect(() => { if (isSuperuser) loadRequests('pending'); }, [isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async (id) => {
    try {
      setRequestActionId(id);
      setRequestsMessage(null);
      const overrideName = approveOrgName.trim();
      const res = await accessRequestService.approve(id, overrideName || null);
      const orgName = res.data?.organization?.name;
      setRequestsMessage({ type: 'success', text: `✓ Approved — organization "${orgName}" is ready.` });
      setApprovingId(null);
      setApproveOrgName('');
      loadRequests();
      loadOrgs();
    } catch (err) {
      setRequestsMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to approve request' });
    } finally {
      setRequestActionId(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setRequestActionId(id);
      setRequestsMessage(null);
      await accessRequestService.reject(id, rejectNote.trim() || null);
      setRequestsMessage({ type: 'success', text: '✓ Request rejected.' });
      setRejectingId(null);
      setRejectNote('');
      loadRequests();
    } catch (err) {
      setRequestsMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to reject request' });
    } finally {
      setRequestActionId(null);
    }
  };

  const ORG_TYPE_LABELS = {
    law_enforcement: 'Law Enforcement',
    financial_institution: 'Financial Institution',
    exchange_vasp: 'Crypto Exchange / VASP',
    compliance_investigation: 'Compliance / Investigation Firm',
    legal: 'Legal',
    other: 'Other',
  };

  const FEATURE_LABELS = {
    hop_tracing: 'Hop Tracing',
    address_clustering: 'Address Clustering',
    risk_scoring: 'Risk Scoring',
    wallet_monitoring: 'Wallet Monitoring',
    defi_decoding: 'DeFi Decoding',
    ai_assistant: 'AI Investigation Assistant',
    statement_parsing: 'Statement Parsing',
    report_generation: 'Report Generation',
    multi_chain_coverage: 'Multi-Chain Coverage',
  };

  const pendingRequestCount = requests.filter(r => r.status === 'pending').length;

  const loadOrgs = useCallback(async () => {
    if (!isSuperuser) return;
    try {
      setOrgsLoading(true);
      setError(null);
      const res = await organizationService.list();
      setOrgs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setOrgsLoading(false);
    }
  }, [isSuperuser]);

  useEffect(() => {
    if (activeTab === 'organizations') loadOrgs();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load once up front too, so the organization_id dropdown in the create-user
  // form is populated even before the Superuser ever visits the Organizations tab.
  useEffect(() => { if (isSuperuser) loadOrgs(); }, [isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrg.name.trim()) return;
    try {
      setCreatingOrg(true);
      setError(null);
      await organizationService.create({
        name: newOrg.name.trim(),
        website: newOrg.website.trim() || undefined,
        contact_email: newOrg.contact_email.trim() || undefined,
      });
      setNewOrg({ name: '', website: '', contact_email: '' });
      loadOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSaveOrgEdit = async (id) => {
    if (!editValues.name.trim()) return;
    try {
      setError(null);
      await organizationService.update(id, {
        name: editValues.name.trim(),
        website: editValues.website.trim(),
        contact_email: editValues.contact_email.trim(),
      });
      setEditingOrgId(null);
      loadOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update organization');
    }
  };

  const handleToggleOrgActive = async (org) => {
    try {
      setError(null);
      await organizationService.update(org.id, { is_active: !org.is_active });
      loadOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update organization status');
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Delete organization "${org.name}"? This only works if it has no users left.`)) return;
    try {
      setError(null);
      await organizationService.delete(org.id);
      loadOrgs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const loadDemoAccounts = useCallback(async () => {
    if (!isSuperuser) return;
    try {
      setDemoLoading(true);
      setError(null);
      const res = await demoAdminService.list();
      setDemoAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load demo accounts');
    } finally {
      setDemoLoading(false);
    }
  }, [isSuperuser]);

  const loadDemoFeedback = useCallback(async () => {
    if (!isSuperuser) return;
    try {
      setDemoFeedbackLoading(true);
      const res = await demoAdminService.listFeedback();
      setDemoFeedback(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load demo feedback');
    } finally {
      setDemoFeedbackLoading(false);
    }
  }, [isSuperuser]);

  useEffect(() => {
    if (activeTab === 'demo') { loadDemoAccounts(); loadDemoFeedback(); }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseDemo = async (d) => {
    if (!window.confirm(`Close the demo trial for "${d.username}" (${d.email})? They'll be logged out and unable to log back in.`)) return;
    try {
      setDemoActionId(d.id);
      setDemoMessage(null);
      await demoAdminService.close(d.id);
      setDemoMessage({ type: 'success', text: `✓ Closed ${d.username}'s trial.` });
      loadDemoAccounts();
    } catch (err) {
      setDemoMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to close demo account' });
    } finally {
      setDemoActionId(null);
    }
  };

  const handleOpenDemo = async (d) => {
    try {
      setDemoActionId(d.id);
      setDemoMessage(null);
      await demoAdminService.open(d.id);
      setDemoMessage({ type: 'success', text: `✓ Re-opened ${d.username}'s trial.` });
      loadDemoAccounts();
    } catch (err) {
      setDemoMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to open demo account' });
    } finally {
      setDemoActionId(null);
    }
  };

  const handleDeleteDemo = async (d) => {
    if (!window.confirm(`Permanently delete the demo account "${d.username}" (${d.email})? This removes their account and data entirely — this cannot be undone. (The email stays blocked from signing up for another free trial.)`)) return;
    try {
      setDemoActionId(d.id);
      setDemoMessage(null);
      await demoAdminService.remove(d.id);
      setDemoMessage({ type: 'success', text: `✓ Deleted ${d.username}'s demo account.` });
      loadDemoAccounts();
    } catch (err) {
      setDemoMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete demo account' });
    } finally {
      setDemoActionId(null);
    }
  };

  const loadAuditLogs = useCallback(async (filters = auditFilter) => {
    try {
      setAuditLoading(true);
      const params = { limit: 200 };
      if (filters.action)   params.action   = filters.action;
      if (filters.username) params.username = filters.username;
      const res = await auditService.getLogs(params);
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilter]);

  useEffect(() => {
    if (activeTab === 'audit') loadAuditLogs();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePurgeAuditLogs = async () => {
    const days = Number(purgeDays);
    if (!days || days < 1) {
      setPurgeMessage({ type: 'error', text: 'Enter a valid number of days (1 or more).' });
      return;
    }
    if (!window.confirm(
      `Permanently delete all audit log entries older than ${days} day(s)? This cannot be undone.`
    )) return;
    try {
      setPurging(true);
      setPurgeMessage(null);
      const res = await auditService.purgeOlderThan(days);
      setPurgeMessage({ type: 'success', text: `✓ Deleted ${res.data.deleted} entr${res.data.deleted === 1 ? 'y' : 'ies'} older than ${days} day(s).` });
      loadAuditLogs();
    } catch (err) {
      setPurgeMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to purge audit logs' });
    } finally {
      setPurging(false);
    }
  };

  const loadApiKeys = useCallback(async () => {
    try {
      setApiKeysLoading(true);
      setApiKeysMessage(null);
      const res = await apiKeysService.get();
      setApiKeys(res.data || {});
      setApiKeyInputs({});
    } catch (err) {
      setApiKeysMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load API keys' });
    } finally {
      setApiKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'apikeys') loadApiKeys();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [apiKeyDeleting, setApiKeyDeleting] = useState(null); // key currently being deleted, for a per-row spinner/disable

  const handleApiKeyDelete = async (key, label) => {
    if (!window.confirm(`Delete the stored ${label || key} key? This removes it entirely -- the field will show "Not set" until a new key is entered.`)) return;
    try {
      setApiKeyDeleting(key);
      setApiKeysMessage(null);
      const res = await apiKeysService.delete(key);
      setApiKeys(res.data.keys || {});
      setApiKeyInputs(prev => { const next = { ...prev }; delete next[key]; return next; });
      setApiKeysMessage({ type: 'success', text: `✓ Deleted ${key} -- takes effect immediately, no restart needed.` });
    } catch (err) {
      setApiKeysMessage({ type: 'error', text: err.response?.data?.detail || `Failed to delete ${key}` });
    } finally {
      setApiKeyDeleting(null);
    }
  };

  const handleApiKeySave = async () => {
    // Only send keys the admin actually typed something into — leaving a field
    // blank keeps its current value unchanged (server-side behaviour, mirrored
    // here so we don't send empty strings that would be silently ignored anyway).
    const payload = {};
    for (const [key, val] of Object.entries(apiKeyInputs)) {
      if (val && val.trim()) payload[key] = val.trim();
    }
    if (Object.keys(payload).length === 0) {
      setApiKeysMessage({ type: 'error', text: 'Enter at least one key before saving.' });
      return;
    }
    try {
      setApiKeysSaving(true);
      setApiKeysMessage(null);
      const res = await apiKeysService.update(payload);
      setApiKeys(res.data.keys || {});
      setApiKeyInputs({});
      const updated = res.data.updated || [];
      setApiKeysMessage({
        type: 'success',
        text: updated.length
          ? `✓ Saved: ${updated.join(', ')} — takes effect immediately, no restart needed.`
          : 'Nothing changed.',
      });
    } catch (err) {
      setApiKeysMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save API keys' });
    } finally {
      setApiKeysSaving(false);
    }
  };

  const isSelf = (u) => String(u.id) === String(userId) || u.username === myUsername;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isSuperuser && newUser.role !== 'superuser' && !newUser.organization_id) {
      setError('Select an organization for this user.');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const payload = {
        ...newUser,
        organization_id: newUser.role === 'superuser' || !newUser.organization_id
          ? undefined
          : Number(newUser.organization_id),
      };
      await adminService.createUser(payload);
      setNewUser({ username: '', email: '', password: '', role: 'investigator', organization_id: '' });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      setError(null);
      await adminService.updateUser(id, { role });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleOrgReassign = async (id, organization_id) => {
    try {
      setError(null);
      await adminService.updateUser(id, { organization_id: Number(organization_id) });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reassign organization');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      setError(null);
      await adminService.updateUser(user.id, { is_active: !user.is_active });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleResetPassword = async (id) => {
    if (!resetPassword.trim()) return;
    try {
      setError(null);
      await adminService.updateUser(id, { password: resetPassword.trim() });
      setResetTarget(null);
      setResetPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      setError(null);
      await adminService.deleteUser(user.id);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const ACTION_LABELS = {
    create_investigation: '🔬 Created investigation',
    delete_investigation: '🗑 Deleted investigation',
    fetch_blockchain:     '🔍 Fetched blockchain',
    import_transactions:  '📂 Imported transactions',
    add_collaborator:     '👥 Added collaborator',
    remove_collaborator:  '❌ Removed collaborator',
    add_note:             '📝 Added note',
    delete_note:          '🗑 Deleted note',
    add_tag:              '🏷️ Added tag',
    purge_audit_logs:     '🧹 Purged audit logs',
    delete_api_key:       '🔑 Deleted API key',
  };

  return (
    <div className="admin-users-page">
      <header className="admin-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">← Back</button>
        <h1>👤 Admin Panel</h1>
        {activeTab === 'users' && (
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? 'Cancel' : '+ New User'}
          </button>
        )}
      </header>

      <div className="admin-tab-bar">
        <button className={`admin-tab-btn${activeTab === 'users' ? ' active' : ''}`}
          onClick={() => setActiveTab('users')}>👤 Users</button>
        {isSuperuser && (
          <button className={`admin-tab-btn${activeTab === 'organizations' ? ' active' : ''}`}
            onClick={() => setActiveTab('organizations')}>🏢 Organizations</button>
        )}
        {isSuperuser && (
          <button className={`admin-tab-btn${activeTab === 'requests' ? ' active' : ''}`}
            onClick={() => setActiveTab('requests')}>
            📝 Access Requests
            {pendingRequestCount > 0 && <span className="tab-badge">{pendingRequestCount}</span>}
          </button>
        )}
        {isSuperuser && (
          <button className={`admin-tab-btn${activeTab === 'demo' ? ' active' : ''}`}
            onClick={() => setActiveTab('demo')}>🆓 Demo Accounts</button>
        )}
        <button className={`admin-tab-btn${activeTab === 'audit' ? ' active' : ''}`}
          onClick={() => setActiveTab('audit')}>📋 Audit Log</button>
        {isSuperuser && (
          <button className={`admin-tab-btn${activeTab === 'apikeys' ? ' active' : ''}`}
            onClick={() => setActiveTab('apikeys')}>🔑 API Keys</button>
        )}
      </div>

      <main className="admin-main">
        {error && <div className="error-message">⚠ {error}</div>}

        {/* ── Users Tab ── */}
        {activeTab === 'users' && (
          <>
            {showForm && (
              <form onSubmit={handleCreate} className="admin-create-form">
                <h3>Create User</h3>
                <div className="form-row">
                  <input type="text" placeholder="Username" required
                    value={newUser.username}
                    onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
                  <input type="email" placeholder="Email" required
                    value={newUser.email}
                    onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                </div>
                <div className="form-row">
                  <input type="password" placeholder="Temporary password" required
                    value={newUser.password}
                    onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                  <select value={newUser.role}
                    onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                    {isSuperuser && <option value="superuser">Superuser</option>}
                    <option value="admin">Admin</option>
                    <option value="investigator">Investigator</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                {isSuperuser && newUser.role !== 'superuser' && (
                  <div className="form-row">
                    <select value={newUser.organization_id}
                      onChange={e => setNewUser({ ...newUser, organization_id: e.target.value })}
                      required>
                      <option value="">Select organization…</option>
                      {orgs.map(o => (
                        <option key={o.id} value={o.id}>{o.name}{!o.is_active ? ' (inactive)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                <p className="field-hint">
                  Hand the username + temporary password to the new user out-of-band (email/Slack/etc.).
                  {isSuperuser && newUser.role === 'superuser' && ' Superusers are platform-wide and belong to no single organization.'}
                </p>
                <button type="submit" disabled={creating} className="btn btn-success">
                  {creating ? 'Creating...' : '✓ Create User'}
                </button>
              </form>
            )}

            {loading ? (
              <p>Loading users...</p>
            ) : (
              <div className={`users-table${isSuperuser ? ' with-org' : ''}`}>
                <div className="users-table-header">
                  <span>Username</span>
                  <span>Email</span>
                  <span>Role</span>
                  {isSuperuser && <span>Organization</span>}
                  <span>Status</span>
                  <span>Created</span>
                  <span>Actions</span>
                </div>
                {users.map(u => (
                  <div key={u.id} className="users-table-row">
                    <span className="u-username">
                      {u.username}
                      {isSelf(u) && <span className="you-badge">you</span>}
                    </span>
                    <span className="u-email">{u.email}</span>
                    <span>
                      <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}>
                        {isSuperuser && <option value="superuser">Superuser</option>}
                        <option value="admin">Admin</option>
                        <option value="investigator">Investigator</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </span>
                    {isSuperuser && (
                      <span>
                        {u.role === 'superuser' ? (
                          <span className="u-org-none">— platform-wide —</span>
                        ) : (
                          <select value={u.organization_id || ''} onChange={e => handleOrgReassign(u.id, e.target.value)}>
                            {orgs.map(o => (
                              <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                          </select>
                        )}
                      </span>
                    )}
                    <span>
                      <button
                        onClick={() => handleToggleActive(u)}
                        className={`status-toggle ${u.is_active ? 'active' : 'disabled'}`}
                      >
                        {u.is_active ? 'Active' : 'Disabled'}
                      </button>
                    </span>
                    <span className="u-created">{new Date(u.created_at).toLocaleDateString()}</span>
                    <span className="u-actions">
                      {resetTarget === u.id ? (
                        <div className="reset-inline">
                          <input
                            type="password"
                            placeholder="New password"
                            value={resetPassword}
                            onChange={e => setResetPassword(e.target.value)}
                          />
                          <button onClick={() => handleResetPassword(u.id)} className="btn-submit">Save</button>
                          <button onClick={() => { setResetTarget(null); setResetPassword(''); }} className="btn-cancel">×</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => setResetTarget(u.id)} className="btn-secondary-sm">Reset PW</button>
                          <button
                            onClick={() => handleDelete(u)}
                            disabled={isSelf(u)}
                            className="btn-delete"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Organizations Tab (Superuser only) ── */}
        {activeTab === 'organizations' && isSuperuser && (
          <div className="orgs-section">
            <form onSubmit={handleCreateOrg} className="admin-create-form orgs-create-form">
              <h3>New Organization</h3>
              <div className="form-row">
                <input type="text" placeholder="Organization name" required
                  value={newOrg.name}
                  onChange={e => setNewOrg({ ...newOrg, name: e.target.value })} />
              </div>
              <div className="form-row">
                <input type="text" placeholder="Website (e.g. acmeforensics.com) — optional"
                  value={newOrg.website}
                  onChange={e => setNewOrg({ ...newOrg, website: e.target.value })} />
                <input type="email" placeholder="Contact email — optional"
                  value={newOrg.contact_email}
                  onChange={e => setNewOrg({ ...newOrg, contact_email: e.target.value })} />
              </div>
              <p className="field-hint">
                Website + contact email appear as the letterhead on this organization's generated
                investigation reports. Leave blank to use Noctis Ledger's own branding instead.
              </p>
              <button type="submit" disabled={creatingOrg} className="btn btn-success">
                {creatingOrg ? 'Creating...' : '✓ Create Organization'}
              </button>
            </form>

            {orgsLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading organizations...</p>
            ) : orgs.length === 0 ? (
              <p style={{ color: '#64748b', padding: '20px 0' }}>No organizations yet — create one above.</p>
            ) : (
              <div className="orgs-table">
                <div className="orgs-table-header">
                  <span>Name</span>
                  <span>Letterhead Branding</span>
                  <span>Users</span>
                  <span>Status</span>
                  <span>Actions</span>
                </div>
                {orgs.map(org => (
                  editingOrgId === org.id ? (
                    <div key={org.id} className="orgs-table-row orgs-table-row-editing">
                      <div className="form-row">
                        <input type="text" placeholder="Organization name" value={editValues.name}
                          onChange={e => setEditValues({ ...editValues, name: e.target.value })} autoFocus />
                      </div>
                      <div className="form-row">
                        <input type="text" placeholder="Website — blank uses Noctis Ledger default"
                          value={editValues.website}
                          onChange={e => setEditValues({ ...editValues, website: e.target.value })} />
                        <input type="email" placeholder="Contact email — blank uses Noctis Ledger default"
                          value={editValues.contact_email}
                          onChange={e => setEditValues({ ...editValues, contact_email: e.target.value })} />
                      </div>
                      <div className="u-actions">
                        <button onClick={() => handleSaveOrgEdit(org.id)} className="btn-submit">Save</button>
                        <button onClick={() => setEditingOrgId(null)} className="btn-cancel">×</button>
                      </div>
                    </div>
                  ) : (
                    <div key={org.id} className="orgs-table-row">
                      <span className="org-name">{org.name}</span>
                      <span className="org-branding">
                        {org.website || org.contact_email ? (
                          <>
                            {org.website && <span className="org-branding-line">{org.website}</span>}
                            {org.contact_email && <span className="org-branding-line">{org.contact_email}</span>}
                          </>
                        ) : (
                          <span className="u-org-none">— uses Noctis Ledger default —</span>
                        )}
                      </span>
                      <span className="org-user-count">{org.user_count}</span>
                      <span>
                        <button
                          onClick={() => handleToggleOrgActive(org)}
                          className={`status-toggle ${org.is_active ? 'active' : 'disabled'}`}
                        >
                          {org.is_active ? 'Active' : 'Disabled'}
                        </button>
                      </span>
                      <span className="u-actions">
                        <button onClick={() => {
                          setEditingOrgId(org.id);
                          setEditValues({ name: org.name, website: org.website || '', contact_email: org.contact_email || '' });
                        }} className="btn-secondary-sm">Edit</button>
                        <button
                          onClick={() => handleDeleteOrg(org)}
                          disabled={org.user_count > 0}
                          title={org.user_count > 0 ? 'Remove all users from this organization first' : ''}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      </span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Access Requests Tab (Superuser only) ── */}
        {activeTab === 'requests' && isSuperuser && (
          <div className="requests-section">
            <p className="apikeys-intro">
              Public applications submitted via the marketing site's Request Access form.
              Approving one creates the matching Organization; rejecting just records the decision.
            </p>

            <div className="audit-filters">
              <select
                className="audit-filter-select"
                value={requestsFilter}
                onChange={e => { setRequestsFilter(e.target.value); loadRequests(e.target.value); }}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="">All</option>
              </select>
              <button className="btn btn-secondary" onClick={() => loadRequests()}>↻ Refresh</button>
            </div>

            {requestsMessage && (
              <div className={requestsMessage.type === 'error' ? 'error-message' : 'success-message'}>
                {requestsMessage.type === 'error' ? '⚠ ' : ''}{requestsMessage.text}
              </div>
            )}

            {requestsLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading access requests...</p>
            ) : requests.length === 0 ? (
              <p style={{ color: '#64748b', padding: '20px 0' }}>
                No {requestsFilter ? `${requestsFilter} ` : ''}access requests.
              </p>
            ) : (
              <div className="requests-list">
                {requests.map(r => (
                  <div key={r.id} className={`request-card request-status-${r.status}`}>
                    <div className="request-card-top">
                      <div className="request-org">
                        <span className="request-org-name">{r.org_name}</span>
                        <span className="request-org-type">{ORG_TYPE_LABELS[r.org_type] || r.org_type}</span>
                      </div>
                      <span className={`request-status-badge status-${r.status}`}>{r.status}</span>
                    </div>

                    <div className="request-card-body">
                      <div className="request-field">
                        <span className="request-field-label">Contact</span>
                        <span>{r.contact_name} &mdash; {r.email}{r.phone ? ` · ${r.phone}` : ''}</span>
                      </div>
                      <div className="request-field">
                        <span className="request-field-label">Plan</span>
                        <span className="request-plan-badge">{r.requested_plan}</span>
                        {r.seats_estimate ? <span className="request-seats"> · ~{r.seats_estimate} users</span> : null}
                      </div>
                      {r.requested_features?.length > 0 && (
                        <div className="request-field">
                          <span className="request-field-label">Features</span>
                          <div className="request-feature-chips">
                            {r.requested_features.map(f => (
                              <span key={f} className="request-feature-chip">{FEATURE_LABELS[f] || f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {r.notes && (
                        <div className="request-field">
                          <span className="request-field-label">Notes</span>
                          <span className="request-notes">{r.notes}</span>
                        </div>
                      )}
                      <div className="request-field">
                        <span className="request-field-label">Submitted</span>
                        <span className="u-created">{formatDate(r.created_at)}{r.ip_address ? ` · ${r.ip_address}` : ''}</span>
                      </div>
                      {r.status !== 'pending' && (
                        <div className="request-field">
                          <span className="request-field-label">Reviewed</span>
                          <span className="u-created">
                            {r.reviewed_at ? formatDate(r.reviewed_at) : '—'}
                            {r.review_note ? ` — "${r.review_note}"` : ''}
                          </span>
                        </div>
                      )}
                    </div>

                    {r.status === 'pending' && (
                      <div className="request-card-actions">
                        {approvingId === r.id ? (
                          <div className="reset-inline">
                            <input
                              type="text"
                              placeholder={r.org_name}
                              value={approveOrgName}
                              onChange={e => setApproveOrgName(e.target.value)}
                              autoFocus
                            />
                            <button className="btn-submit" disabled={requestActionId === r.id}
                              onClick={() => handleApprove(r.id)}>
                              {requestActionId === r.id ? 'Approving...' : 'Confirm'}
                            </button>
                            <button className="btn-cancel"
                              onClick={() => { setApprovingId(null); setApproveOrgName(''); }}>×</button>
                          </div>
                        ) : rejectingId === r.id ? (
                          <div className="reset-inline">
                            <input
                              type="text"
                              placeholder="Reason (optional)"
                              value={rejectNote}
                              onChange={e => setRejectNote(e.target.value)}
                              autoFocus
                            />
                            <button className="btn-delete" disabled={requestActionId === r.id}
                              onClick={() => handleReject(r.id)}>
                              {requestActionId === r.id ? 'Rejecting...' : 'Confirm'}
                            </button>
                            <button className="btn-cancel"
                              onClick={() => { setRejectingId(null); setRejectNote(''); }}>×</button>
                          </div>
                        ) : (
                          <>
                            <button className="btn-submit"
                              onClick={() => { setApprovingId(r.id); setApproveOrgName(r.org_name); setRejectingId(null); }}>
                              ✓ Approve
                            </button>
                            <button className="btn-delete"
                              onClick={() => { setRejectingId(r.id); setRejectNote(''); setApprovingId(null); }}>
                              ✕ Reject
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Demo Accounts Tab (Superuser only) ── */}
        {activeTab === 'demo' && isSuperuser && (
          <div className="demo-section">
            <p className="apikeys-intro">
              Free 5-day trial accounts, one per email address (enforced permanently, even
              after an account is closed). Close a trial to disable it immediately; open a
              closed or expired trial to reactivate it — if its 5 days already ran out,
              reopening grants a fresh 5 days.
            </p>

            {demoMessage && (
              <div className={demoMessage.type === 'error' ? 'error-message' : 'success-message'}>
                {demoMessage.type === 'error' ? '⚠ ' : ''}{demoMessage.text}
              </div>
            )}

            {demoLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading demo accounts...</p>
            ) : demoAccounts.length === 0 ? (
              <p style={{ color: '#64748b', padding: '20px 0' }}>No demo trials signed up yet.</p>
            ) : (
              <div className="users-table">
                <div className="users-table-header">
                  <span>Username</span>
                  <span>Email</span>
                  <span>Status</span>
                  <span>Started</span>
                  <span>Expires</span>
                  <span>Actions</span>
                </div>
                {demoAccounts.map(d => (
                  <div key={d.id} className="users-table-row">
                    <span className="u-username">{d.username}</span>
                    <span className="u-email">{d.email}</span>
                    <span>
                      <span className={`request-status-badge status-${d.status === 'active' ? 'approved' : d.status === 'closed' ? 'rejected' : 'pending'}`}>
                        {d.status}
                      </span>
                    </span>
                    <span className="u-created">{formatDate(d.started_at)}</span>
                    <span className="u-created">{formatDate(d.expires_at)}</span>
                    <span className="u-actions">
                      {d.status === 'active' ? (
                        <button className="btn-delete" disabled={demoActionId === d.id}
                          onClick={() => handleCloseDemo(d)}>
                          {demoActionId === d.id ? 'Closing...' : 'Close'}
                        </button>
                      ) : (
                        <button className="btn-submit" disabled={demoActionId === d.id}
                          onClick={() => handleOpenDemo(d)}>
                          {demoActionId === d.id ? 'Opening...' : 'Open'}
                        </button>
                      )}
                      <button className="btn-delete" disabled={demoActionId === d.id}
                        title="Permanently delete this account (harsher than Close)"
                        onClick={() => handleDeleteDemo(d)}>
                        {demoActionId === d.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <h3 style={{ marginTop: 32 }}>💬 Demo Feedback</h3>
            {demoFeedbackLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading feedback...</p>
            ) : demoFeedback.length === 0 ? (
              <p style={{ color: '#64748b', padding: '20px 0' }}>No feedback submitted yet.</p>
            ) : (
              <div className="requests-list">
                {demoFeedback.map(f => (
                  <div key={f.id} className="request-card">
                    <div className="request-card-top">
                      <div className="request-org">
                        <span className="request-org-name">{f.username || '—'}</span>
                        <span className="request-org-type">{f.email || '—'}</span>
                      </div>
                      <span className="u-created">{formatDate(f.created_at)}</span>
                    </div>
                    <div className="request-card-body">
                      <span className="request-notes">{f.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Audit Log Tab ── */}
        {activeTab === 'audit' && (
          <div className="audit-log-section">
            <div className="audit-filters">
              <input
                className="audit-filter-input"
                placeholder="Filter by username"
                value={auditFilter.username}
                onChange={e => setAuditFilter(f => ({ ...f, username: e.target.value }))}
              />
              <select
                className="audit-filter-select"
                value={auditFilter.action}
                onChange={e => setAuditFilter(f => ({ ...f, action: e.target.value }))}
              >
                <option value="">All actions</option>
                {Object.keys(ACTION_LABELS).map(a => (
                  <option key={a} value={a}>{ACTION_LABELS[a]}</option>
                ))}
              </select>
              <button className="btn btn-primary"
                onClick={() => loadAuditLogs(auditFilter)}>
                🔍 Search
              </button>
              <button className="btn btn-secondary"
                onClick={() => {
                  const cleared = { action: '', username: '' };
                  setAuditFilter(cleared);
                  loadAuditLogs(cleared);
                }}>
                ✕ Clear
              </button>
              <span className="audit-total">{auditTotal} entries</span>
            </div>

            {isSuperuser && (
              <div className="audit-purge-row">
                <span className="audit-purge-label">🧹 Purge entries older than</span>
                <input
                  type="number"
                  min="1"
                  className="audit-purge-input"
                  value={purgeDays}
                  onChange={e => setPurgeDays(e.target.value)}
                />
                <span className="audit-purge-label">days</span>
                <button className="btn btn-delete" disabled={purging} onClick={handlePurgeAuditLogs}>
                  {purging ? 'Purging...' : 'Purge'}
                </button>
                {purgeMessage && (
                  <span className={purgeMessage.type === 'error' ? 'error-message' : 'success-message'}>
                    {purgeMessage.type === 'error' ? '⚠ ' : ''}{purgeMessage.text}
                  </span>
                )}
              </div>
            )}

            {auditLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading audit log...</p>
            ) : auditLogs.length === 0 ? (
              <p style={{ color: '#64748b', padding: '20px 0' }}>No audit log entries found.</p>
            ) : (
              <div className="audit-table">
                <div className="audit-table-header">
                  <span>Timestamp</span>
                  <span>User</span>
                  <span>Action</span>
                  <span>Target</span>
                  <span>Detail</span>
                </div>
                {auditLogs.map(log => (
                  <div key={log.id} className={`audit-table-row audit-action-${log.action}`}>
                    <span className="audit-ts">{formatDate(log.created_at)}</span>
                    <span className="audit-user">{log.username || '—'}</span>
                    <span className="audit-action">
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                    <span className="audit-target">
                      {log.target_type && log.target_id
                        ? `${log.target_type} #${log.target_id}`
                        : '—'}
                    </span>
                    <span className="audit-detail">{log.detail || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── API Keys Tab ── */}
        {activeTab === 'apikeys' && (
          <div className="apikeys-section">
            <p className="apikeys-intro">
              Blockchain explorer API keys used to fetch on-chain data. Saving here applies
              immediately to the running backend — no restart needed. Leave a field blank to
              keep its current value unchanged.
            </p>

            {apiKeysMessage && (
              <div className={apiKeysMessage.type === 'error' ? 'error-message' : 'success-message'}>
                {apiKeysMessage.type === 'error' ? '⚠ ' : ''}{apiKeysMessage.text}
              </div>
            )}

            {apiKeysLoading ? (
              <p style={{ color: '#88aabb', padding: '20px 0' }}>Loading API key status...</p>
            ) : (
              <>
                <div className="apikeys-table">
                  {Object.entries(apiKeys).map(([key, info]) => (
                    <div key={key} className="apikeys-row">
                      <div className="apikeys-row-label">
                        <span className="apikeys-key-name">{key}</span>
                        <span className={`apikeys-status ${info.configured ? 'configured' : 'missing'}`}>
                          {info.configured ? `✓ Configured (${info.masked})` : '✕ Not set'}
                        </span>
                        <span className="apikeys-hint">{info.label}{info.hint ? ` — ${info.hint}` : ''}</span>
                      </div>
                      <div className="apikeys-row-controls">
                        <input
                          type="password"
                          className="apikeys-input"
                          placeholder={info.configured ? 'Leave blank to keep current key' : 'Enter API key'}
                          value={apiKeyInputs[key] || ''}
                          onChange={e => setApiKeyInputs(prev => ({ ...prev, [key]: e.target.value }))}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="btn-delete"
                          disabled={!info.configured || apiKeyDeleting === key}
                          title={info.configured ? `Delete stored ${info.label}` : 'Nothing stored to delete'}
                          onClick={() => handleApiKeyDelete(key, info.label)}
                        >
                          {apiKeyDeleting === key ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-success"
                  disabled={apiKeysSaving}
                  onClick={handleApiKeySave}
                >
                  {apiKeysSaving ? 'Saving...' : '✓ Save API Keys'}
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
